import io, { Socket } from 'socket.io-client';
import { useState, useEffect, useRef, useCallback } from 'react';

import { useDebounceFn } from '@frontend/features/common/hooks/useDebounceFn';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { CONFIG } from '@frontend/config';

export type SendMessageCallback = (event: string, data: string) => void;

interface UseWsOptions {
  onConnected?: () => void;
  enabled?: boolean;
}

export const useWS = (url: string, options?: UseWsOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuth();
  const socketRef = useRef<Socket>();
  const onConnected = options?.onConnected;
  const enabled = options?.enabled ?? true;
  const onConnectedRef = useRef(onConnected);

  onConnectedRef.current = onConnected;

  const teardownSocket = useCallback((socket = socketRef.current) => {
    const isActiveSocket = socketRef.current === socket;

    if (!socket) {
      return;
    }

    if (isActiveSocket) {
      socketRef.current = undefined;
    }

    socket.removeAllListeners();
    socket.disconnect();
    if (isActiveSocket) {
      setIsConnected(false);
    }
  }, []);

  const connect = useCallback(() => {
    if (socketRef.current) {
      teardownSocket();
    }
    const socket = io(url, {
      transports: ['websocket'],
      path: '/v1/api/socket.io',
      query: { agencyId: CONFIG.AGENCY_ID },
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      if (socketRef.current !== socket) {
        return;
      }

      console.log('WebSocket connected');
      setIsConnected(true);
      onConnectedRef.current && onConnectedRef.current();
    });

    socket.on('disconnect', () => {
      if (socketRef.current !== socket) {
        return;
      }

      console.log('WebSocket disconnected');
      setIsConnected(false);
    });

    socket.on('connect_error', (err: Error) => {
      if (socketRef.current !== socket) {
        return;
      }

      console.error('WebSocket error', err);
      setIsConnected(false);
    });
  }, [teardownSocket, url]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('Manually disconnecting WebSocket');
      teardownSocket();
    } else {
      console.log('No active WebSocket connection to disconnect');
    }
  }, [teardownSocket]);

  const sendMessage = useCallback<SendMessageCallback>(
    (event, data) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit(event, data);
      } else {
        console.error('Socket is not connected');
      }
    },
    [isConnected],
  );

  const joinRoom = useCallback(
    (roomId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('joinRoom', roomId);
      } else {
        console.error('Socket is not connected');
      }
    },
    [isConnected, socketRef],
  );

  const leaveRoom = useCallback(
    (roomId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('leaveRoom', roomId);
      } else {
        console.error('Socket is not connected');
      }
    },
    [isConnected],
  );

  useEffect(() => {
    console.log('[useWS] useEffect mount');

    if (!isAuthenticated || !enabled) {
      console.log('[useWS] skipping connect: not authenticated');
      return;
    }

    connect();

    return () => {
      console.log('[useWS] useEffect cleanup (unmount)');
      teardownSocket();
    };
  }, [url, isAuthenticated, enabled, connect, teardownSocket]);

  const sendMessageDebounced = useDebounceFn(sendMessage, 500);

  return { isConnected, sendMessage, sendMessageDebounced, connect, disconnect, socketRef, joinRoom, leaveRoom };
};
